import { Logger } from '@lambda/commons/logging/logger';
import axios from 'axios';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import settings from '@/settings';
import i18n from '@/settings/i18n';

const logger = new Logger('warranty-api');

export const getWarrantyInfoByCategoryPks = async (categoryPks: string[]) => {
  try {
    const response = await axios.post<ProductCategoryWarranty[]>(
      '/api/warranty',
      { categoryPks },
    );

    const warrantyInfoArray = response.data;

    return warrantyInfoArray;
  } catch (err: any) {
    logger.error({ err }, 'error fetching warranty info by category pks');

    return null;
  }
};

// Localized "{n} months/years/…" — keys live in shared.json so they resolve
// from any namespace.
const formatWarrantyDuration = (
  t: ReturnType<typeof i18n.init>,
  unit: string,
  length: number,
) => {
  const plural = length > 1;

  switch (unit.toLowerCase()) {
    case 'year':
      return plural
        ? t`${{ count: length }} Years`
        : t`${{ count: length }} Year`;
    case 'week':
      return plural
        ? t`${{ count: length }} Weeks`
        : t`${{ count: length }} Week`;
    case 'day':
      return plural
        ? t`${{ count: length }} Days`
        : t`${{ count: length }} Day`;
    case 'month':
    default:
      return plural
        ? t`${{ count: length }} Months`
        : t`${{ count: length }} Month`;
  }
};

export const formatWarrantyInfo = (
  warrantyInfo?: ProductCategoryWarranty | null,
  mixedCart?: boolean,
) => {
  const t = i18n.init('shared');

  const eligible =
    warrantyInfo &&
    warrantyInfo.eligible &&
    warrantyInfo.warrantyLength &&
    warrantyInfo.warrantyUnit;

  const length = eligible
    ? warrantyInfo.warrantyLength ?? settings.warranty_month
    : settings.warranty_month;
  const unit = (eligible ? warrantyInfo.warrantyUnit : 'month') ?? 'month';

  const duration = formatWarrantyDuration(t, unit, length);

  return mixedCart ? t`Up to ${{ duration }}` : duration;
};

export const normalizeWarrantyLength = (
  length?: number,
  unit?: string,
): number => {
  if (!unit || !length) {
    return 0;
  }

  switch (unit) {
    case 'day':
      return length;
    case 'month':
      return length * 30; // Approximation: 1 month = 30 days
    case 'year':
      return length * 365; // Approximation: 1 year = 365 days
    default:
      return 0;
  }
};
